import RefreshIcon from '@mui/icons-material/Refresh';
import Search from '@mui/icons-material/Search';
import { Grid, Stack, Box, Tooltip } from '@mui/material';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import isEmpty from 'lodash-es/isEmpty';
import { useEffect, useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getInboxEmailBody,
  amplitude,
  IEmailInboxRecordsDTO,
  getInboxPaginated,
  getPreviewForEmailBody,
} from 'wisestep-api';
import { WsButton, Item, WsColors, WsCard, CardVariant } from 'wisestep-base';

import { useWorkspaceContext } from '../../../../context';
import { warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { InboxEmailLogsType } from '../../hooks/useEmailLogs';

import { InboxContent } from './components/InboxContent';
import { InboxListItem } from './components/InboxListItem';
import { InboxPagination } from './components/InboxPagination';
import {
  defaultInboxData,
  InboxEmailPropTypes,
  singleDataType,
  InBoxContentData,
  handleFilterAndPageType,
  defaultInboxLogs,
  extractEmail,
  singleDataHandleType,
  InboxActionType,
} from './helper';
import SearchEmailDialog from './searchEmails/SearchEmailDialog';
import { useNewEmailStore } from './Util/NewEmailStore';

const InboxEmailLogs = ({
  onActionCallback,
  hasAttachmentsCallback,
  getInboxStats,
}: InboxEmailPropTypes) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const setInBoxData = useNewEmailStore((state) => state.setInBoxData);
  const configuredImapAccounts = useNewEmailStore((state) => state.configuredImapAccounts);
  const getInboxData = useNewEmailStore((state) => state.inboxData);
  const workspace = useWorkspaceContext();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [handleContent, setHandleContent] = useState<singleDataType>({
    content: defaultInboxData,
    showDrawer: false,
  });
  const [singleDataHandle, setSingleDataHandle] = useState<singleDataHandleType>({
    configuredAccounts: [],
    inboxData: defaultInboxLogs,
  });

  const [handleFilterAndPage, setHandleFilterAndPage] = useState<handleFilterAndPageType>({
    filter: { filter: 'ALL', seen: '' },
    page: 1,
  });

  const filterArray = ['ALL', 'READ', 'UNREAD'];

  const inboxAmplitudeEvent = (name: string, clickedOnName: string) =>
    amplitude(name, {
      clickedOn: clickedOnName,
      workspaceName: workspace.name,
      pageName: 'Mail',
      planType: workspace.planType,
      interfaceType: 'web',
      pageUrl: window.location.href,
    });

  const replyRecipientsGenerator = () =>
    `${extractEmail(handleContent.content?.from ?? '').email},${handleContent.content?.to ?? ''}`
      .split(',')
      .filter((single) => single != singleDataHandle.configuredAccounts[0].recruiterEmail);

  const replyAllRecipientsGenerator = () =>
    `${handleContent.content?.ccList},${extractEmail(handleContent.content?.from ?? '').email},${
      handleContent.content?.to ?? ''
    }`
      .split(',')
      .map((email) => extractEmail(email).email)
      .filter((single) => single != singleDataHandle.configuredAccounts[0].recruiterEmail);
  const inboxActionHandler = (button: string) => {
    const payload: InboxActionType = {
      subject: '',
      to: [],
      body: '',
      showForm: false,
    };
    const buttonAction = {
      ['Reply']: () => {
        inboxAmplitudeEvent('Inbox_Reply', 'Reply');
        payload.subject = `RE:${handleContent.content?.subject ?? ''}`;
        payload.to = replyRecipientsGenerator();
        payload.showForm = true;
        onActionCallback(payload);
      },
      ['Reply All']: () => {
        inboxAmplitudeEvent('Inbox_Reply_all', 'Reply All');
        payload.subject = `RE:${handleContent.content?.subject ?? ''}`;
        payload.to = replyAllRecipientsGenerator();
        payload.showForm = true;
        onActionCallback(payload);
      },
      ['Forward']: () => {
        inboxAmplitudeEvent('Inbox_Forward', 'Forward');
        delete payload.to;
        delete payload.subject;
        payload.body = `<br><br><br><br><br><br>---------- Forwarded message --------- <br> From: ${
          handleContent.content?.from
        } <br> Date: ${new Date(handleContent.content?.sent_on ?? '').toString()} <br>Subject: ${
          handleContent.content?.subject ?? ''
        } <br>To: ${handleContent.content?.to ?? ''} <br>Cc: ${
          handleContent.content?.to ?? ''
        }<b><br> ${handleContent.content?.extractedBody ?? ''}`;

        payload.showForm = true;
        onActionCallback(payload);
        hasAttachmentsCallback &&
          hasAttachmentsCallback(isEmpty(handleContent.content?.attachments) ? false : true);
      },
    };
    buttonAction[button]();
  };
  useEffect(() => {
    const loadData = async () => {
      if (!isEmpty(configuredImapAccounts)) {
        getInboxStats && getInboxStats(configuredImapAccounts[0].recruiterEmail);

        let email: IEmailInboxRecordsDTO;
        let emailsData: InboxEmailLogsType;
        let bodyData;

        if (handleFilterAndPage.page - 1 === 0) {
          emailsData = getInboxData;
          email = emailsData.records[0];
          emailsData.records[0].seen = true;

          setSingleDataHandle({
            configuredAccounts: configuredImapAccounts,
            inboxData: emailsData,
          });
          const res = await getInboxEmailBody(
            configuredImapAccounts[0].recruiterEmail,
            emailsData.records[0].messageId
          );
          bodyData = res.data;
        } else {
          const loadDataFirstTime = await getInboxPaginated(
            configuredImapAccounts[0].recruiterEmail,
            handleFilterAndPage.page - 1,
            handleFilterAndPage.filter.seen
          );
          const records = loadDataFirstTime.data;
          records.records[0].seen = true;
          setSingleDataHandle({
            configuredAccounts: configuredImapAccounts,
            inboxData: records,
          });
          email = records.records[0];
          const res = await getInboxEmailBody(
            configuredImapAccounts[0].recruiterEmail,
            email.messageId
          );
          bodyData = res.data;
        }
        const getPreviewUrlForBody = await getPreviewForEmailBody(bodyData.body);
        if (getPreviewUrlForBody.data) {
          const selectedContent: InBoxContentData = {
            to: email?.toEmail,
            from: email?.fromEmail,
            subject: email?.subject,
            message: getPreviewUrlForBody.data.previewUrl,
            fromName: email?.fromEmail,
            recruiterId: configuredImapAccounts[0].recruiterEmail,
            recruiterName: configuredImapAccounts[0].recruiterName,
            sent_on: email?.receivedDate,
            attachments: bodyData.attachments ? bodyData.attachments : [],
            messageId: bodyData.messageId,
            bccList: email.bccList,
            ccList: email.ccList,
            extractedBody: getPreviewUrlForBody.data.bodyContent,
          };

          setHandleContent({
            content: selectedContent,
            showDrawer: true,
          });
          hasAttachmentsCallback &&
            hasAttachmentsCallback(isEmpty(selectedContent.attachments) ? false : true);
        }
      }
    };
    loadData();
  }, [
    configuredImapAccounts,
    getInboxData,
    getInboxStats,
    handleFilterAndPage.filter,
    handleFilterAndPage.page,
    hasAttachmentsCallback,
  ]);

  const onMailItemClick = async (email) => {
    if (singleDataHandle.configuredAccounts) {
      const tempData = singleDataHandle;
      const indexofEmail = tempData.inboxData.records.findIndex(
        (singleEmail) => singleEmail.messageId === email.messageId
      );
      if (indexofEmail !== -1) {
        tempData.inboxData.records[indexofEmail].seen = true;

        setSingleDataHandle({ ...tempData });
      }
      const res = await getInboxEmailBody(
        singleDataHandle.configuredAccounts[0].recruiterEmail,
        email.messageId
      );
      const getPreviewUrlForBody = await getPreviewForEmailBody(res.data.body);
      if (getPreviewUrlForBody.data) {
        const selectedContent = {
          to: email?.toEmail,
          from: email?.fromEmail,
          subject: email?.subject,
          message: getPreviewUrlForBody.data.previewUrl,
          fromName: email?.fromEmail,
          recruiterId: singleDataHandle.configuredAccounts[0].recruiterEmail,
          recruiterName: singleDataHandle.configuredAccounts[0].recruiterName,
          sent_on: email?.receivedDate,
          attachments: res.data.attachments ? res.data.attachments : [],
          messageId: email.messageId,
          bccList: email.bcc,
          ccList: email.cc,
          extractedBody: getPreviewUrlForBody.data.bodyContent,
        };
        setHandleContent({
          content: selectedContent,
          showDrawer: true,
        });
        hasAttachmentsCallback &&
          hasAttachmentsCallback(isEmpty(selectedContent.attachments) ? false : true);

        getInboxStats && getInboxStats(singleDataHandle.configuredAccounts[0].recruiterEmail);
      }
    }
  };

  const performFilter = (action: string) => {
    const filterACtion = {
      ['ALL']: () => {
        inboxAmplitudeEvent('Inbox_Filter_All', 'Inbox All');
        setHandleFilterAndPage({ page: 1, filter: { filter: action, seen: '' } });
        loadDataOnFilterChange('');
      },
      ['READ']: () => {
        inboxAmplitudeEvent('Inbox_Filter_Read', 'Inbox Read');
        setHandleFilterAndPage({
          page: 1,
          filter: { filter: action, seen: 'true' },
        });
        loadDataOnFilterChange('true');
      },
      ['UNREAD']: () => {
        inboxAmplitudeEvent('Inbox_Filter_unread', 'Inbox Unread');
        setHandleFilterAndPage({
          page: 1,
          filter: { filter: action, seen: 'false' },
        });
        loadDataOnFilterChange('false');
      },
    };
    filterACtion[action]();
  };
  const loadDataOnFilterChange = async (filter: string) => {
    try {
      const loadDataFirstTime = await getInboxPaginated(
        singleDataHandle.configuredAccounts[0].recruiterEmail,
        handleFilterAndPage.page - 1,
        filter
      );
      const records = loadDataFirstTime.data;
      if (records) {
        if (handleFilterAndPage.page - 1 === 0) {
          setInBoxData(records);
        }
      }
    } catch {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };
  const onRefreshClick = async () => {
    inboxAmplitudeEvent('Inbox_Refresh', 'Refresh');
    try {
      const loadDataFirstTime = await getInboxPaginated(
        singleDataHandle.configuredAccounts[0].recruiterEmail,
        handleFilterAndPage.page - 1,
        handleFilterAndPage.filter.seen
      );
      const records = loadDataFirstTime.data;
      if (records) {
        if (handleFilterAndPage.page - 1 === 0) {
          setInBoxData(records);
        } else {
          setSingleDataHandle((prev) => ({ ...prev, inboxData: records }));
        }
      }
    } catch {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };
  const navigate = useNavigate();

  return (
    <>
      {!isEmpty(configuredImapAccounts) ? (
        !isEmpty(getInboxData.records) ? (
          <>
            <Grid bgcolor="white" direction="row" container>
              <Grid item xs={3}>
                <Stack
                  spacing={1}
                  bgcolor="white"
                  direction="row"
                  justifyContent="flex-start"
                  m={1}
                >
                  {filterArray.map((single, index) => (
                    <WsButton
                      key={index}
                      variant={handleFilterAndPage.filter.filter === single ? 'outlined' : 'text'}
                      onClick={() => performFilter(single)}
                    >
                      {single}
                    </WsButton>
                  ))}
                  <Tooltip title={'Refresh'}>
                    <RefreshIcon
                      onClick={onRefreshClick}
                      sx={{ cursor: 'pointer', color: WsColors.TheBlues400 }}
                    />
                  </Tooltip>
                  {configuredImapAccounts &&
                    configuredImapAccounts[0].emailServer != 'outlook.office365.com' && (
                      <Tooltip title={'Search'}>
                        <WsButton
                          onClick={() => setOpenDialog(true)}
                          variant="outlined"
                          startIcon={<Search />}
                        >
                          Search
                        </WsButton>
                      </Tooltip>
                    )}
                </Stack>
              </Grid>

              <Grid justifyContent="flex-end" item xs={9}>
                {singleDataHandle.inboxData &&
                  singleDataHandle.inboxData.totalCount >= 0 &&
                  singleDataHandle.inboxData.lastIndexRead >= 0 && (
                    <InboxPagination
                      singleDataHandle={singleDataHandle}
                      onMailItemClick={(email) => onMailItemClick(email)}
                      setSingleDataHandle={(data: singleDataHandleType) =>
                        setSingleDataHandle({ ...data })
                      }
                      setHandleFilterAndPage={(data: handleFilterAndPageType) =>
                        setHandleFilterAndPage({ ...data })
                      }
                      handleFilterAndPage={handleFilterAndPage}
                    />
                  )}
              </Grid>
            </Grid>

            <Grid item bgcolor="grey" container xs={12}>
              {singleDataHandle.inboxData &&
              singleDataHandle.inboxData.records &&
              !isEmpty(getInboxData.records) ? (
                <>
                  <Grid item xs={4.5} height={900} sx={{ overflowY: 'auto' }}>
                    <List
                      sx={{
                        bgcolor: 'background.paper',
                        borderColor: 'text.primary',
                      }}
                    >
                      {singleDataHandle.inboxData.records?.map((email, index) => (
                        <>
                          <InboxListItem
                            email={email}
                            index={index}
                            onMailItemClick={(email) => onMailItemClick(email)}
                            handleContent={handleContent}
                          />
                          <Divider />
                        </>
                      ))}
                    </List>
                    {singleDataHandle.inboxData &&
                      singleDataHandle.inboxData.totalCount >= 0 &&
                      singleDataHandle.inboxData.lastIndexRead >= 0 && (
                        <Item sx={{ marginTop: 1 }}>
                          <InboxPagination
                            singleDataHandle={singleDataHandle}
                            onMailItemClick={(email) => onMailItemClick(email)}
                            setSingleDataHandle={(data: singleDataHandleType) =>
                              setSingleDataHandle({ ...data })
                            }
                            setHandleFilterAndPage={(data: handleFilterAndPageType) =>
                              setHandleFilterAndPage({ ...data })
                            }
                            handleFilterAndPage={handleFilterAndPage}
                          />
                        </Item>
                      )}
                  </Grid>
                  {handleContent.showDrawer && (
                    <InboxContent
                      handleContent={handleContent}
                      inboxActionHandler={(name: string) => inboxActionHandler(name)}
                      singleDataHandle={singleDataHandle}
                    />
                  )}
                </>
              ) : (
                <WsCard
                  cardType={CardVariant.WITH_OUT_BG}
                  title="Please wait while we sync your Emails"
                  isButton={true}
                  buttonText={'Refresh'}
                  onClick={() => window.location.reload()}
                />
              )}
            </Grid>
          </>
        ) : (
          <WsCard
            cardType={CardVariant.WITH_OUT_BG}
            title="Please wait while we sync your Emails"
            isButton={true}
            buttonText={'Refresh'}
            onClick={() => window.location.reload()}
          />
        )
      ) : (
        <Box>
          <WsCard
            cardType={CardVariant.WITH_OUT_BG}
            title="No IMAP Configuration Found"
            description=" Please configure one to receive emails!"
            isButton={true}
            onClick={() => navigate('/email-settings/configure-email-inbox/')}
            buttonText="Configure Now"
          />
        </Box>
      )}
      <SearchEmailDialog openDialog={openDialog} closeDialog={() => setOpenDialog(false)} />
    </>
  );
};
export default InboxEmailLogs;
